import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Network, X, ExternalLink, Crosshair, ArrowRight, PenLine, Link2, CornerUpRight, Sparkles, ListPlus, Check, RefreshCw } from "lucide-react";
import { GraphCanvas, useSelection, lightTheme } from "reagraph";
import * as api from "../../lib/api";
import { openSearchTopic } from "../search-performance/TopicParts";

// Search-demand graph (GET /graph/search-demand): search topics → pages that serve them →
// real links between pages. Built from GSC + the site crawl, so it works without a CMS.
const PRIMARY = "#018D4C";
const TIER = {
  high:     { label: "High priority",   color: "#018D4C" },
  medium:   { label: "Medium priority", color: "#71717A" },
  low:      { label: "Low priority",    color: "#D4D4D8" },
  unscored: { label: "Not scored",      color: "#E4E4E7" },
};
const PAGE_COLOR = "#94A3B8";
const KEY_PAGE_COLOR = "#F59E0B";
const OLD_PAGE_COLOR = "#DC2626";
const LINK_COLOR = "#22C55E";
const MISSING_COLOR = "#F97316";
const MOVES = {
  write_new: "Write new page", expand_page: "Expand page", fix_snippet: "Fix title & meta",
  consolidate: "Merge pages", internal_links: "Add internal links", maintain: "Keep fresh", deprioritize: "Deprioritize",
};
const THEME = {
  ...lightTheme,
  canvas: { background: "#ffffff", fog: "#ffffff" },
  node: { ...lightTheme.node, inactiveOpacity: 0.12 },
  edge: { ...lightTheme.edge, inactiveOpacity: 0.06, opacity: 0.7 },
};

const path = (u) => { try { return new URL(u).pathname || "/"; } catch { return u; } };

function TodoCard({ title, icon: Icon, empty, items, render }) {
  return (
    <div className="bg-white rounded-xl border border-zinc-100 shadow-sm p-4">
      <p className="flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500 mb-3">
        <Icon size={11} /> {title} {items.length > 0 && <span className="text-zinc-400">({items.length})</span>}
      </p>
      {items.length === 0
        ? <p className="text-xs text-zinc-400">{empty}</p>
        : <div className="flex flex-col gap-2 max-h-48 overflow-y-auto">{items.slice(0, 8).map(render)}</div>}
    </div>
  );
}

const ago = (iso) => {
  if (!iso) return "";
  const m = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  return m < 1 ? "just now" : m < 60 ? `${m}m ago` : m < 1440 ? `${Math.round(m / 60)}h ago` : `${Math.round(m / 1440)}d ago`;
};

// AI next steps for the selected node (POST /graph/search-demand/insight). Written once
// per node and stored; opening the node again shows the stored plan, Regenerate replaces it.
function NodeInsight({ nodeId }) {
  const [state, setState] = useState({ loading: true });
  // {busy} while creating, then {task, existing} or {error}
  const [taskState, setTaskState] = useState(null);

  const createTask = async () => {
    setTaskState({ busy: true });
    try {
      const r = await api.createTaskFromInsight(nodeId);
      setTaskState({ task: r.task, existing: r.existing });
    } catch (e) {
      setTaskState({ error: e?.response?.data?.detail || e.message || "Could not create task" });
    }
  };

  const aliveRef = useRef(true);
  const load = useCallback((regenerate = false) => {
    setState(prev => ({ loading: true, regenerating: regenerate, data: regenerate ? prev.data : null }));
    if (regenerate) setTaskState(null);
    api.getSearchDemandInsight(nodeId, regenerate)
      .then(d => aliveRef.current && setState({ data: d }))
      .catch(e => aliveRef.current && setState({ error: e?.response?.data?.detail || e.message || "Insight failed" }));
  }, [nodeId]);

  useEffect(() => {
    aliveRef.current = true;
    load(false);
    return () => { aliveRef.current = false; };
  }, [load]);

  return (
    <div className="rounded-lg border border-emerald-100 bg-emerald-50/50 p-3">
      <div className="flex items-center justify-between mb-2">
        <p className="flex items-center gap-1.5 text-[10px] tracking-[0.15em] uppercase font-bold" style={{ color: PRIMARY }}>
          <Sparkles size={11} /> What to do
        </p>
        {(state.data || state.error) && !state.loading && (
          <button onClick={() => load(true)} title="Write a new plan from the current data (uses one AI call)"
            className="inline-flex items-center gap-1 text-[10px] font-bold text-zinc-500 hover:text-zinc-900">
            <RefreshCw size={10} /> Regenerate
          </button>
        )}
      </div>
      {state.data?.stale && !state.loading && (
        <p className="text-[10px] text-amber-700 bg-amber-50 border border-amber-100 rounded px-2 py-1 mb-2">
          Your data changed since this plan was written. Regenerate for an updated plan.
        </p>
      )}
      {state.loading && (
        <div className="space-y-1.5 animate-pulse">
          <div className="h-2.5 bg-emerald-100 rounded w-11/12" />
          <div className="h-2.5 bg-emerald-100 rounded w-9/12" />
          <div className="h-2.5 bg-emerald-100 rounded w-10/12" />
          <p className="text-[10px] text-zinc-400 pt-1">{state.regenerating ? "Writing a new plan…" : "Reading this node's searches, pages and links…"}</p>
        </div>
      )}
      {state.error && <p className="text-[11px] text-red-600">{state.error}</p>}
      {state.data && !state.loading && (
        <>
          {state.data.headline && <p className="text-xs font-bold text-zinc-900 leading-snug mb-2">{state.data.headline}</p>}
          <ol className="space-y-2">
            {state.data.steps.map((st, i) => (
              <li key={i} className="flex gap-2">
                <span className="w-4 h-4 flex-shrink-0 rounded-full text-[9px] font-bold text-white flex items-center justify-center mt-0.5" style={{ background: PRIMARY }}>{i + 1}</span>
                <div className="min-w-0">
                  <p className="text-[11px] text-zinc-800 leading-snug">{st.action}</p>
                  {st.why && <p className="text-[10px] text-zinc-500 leading-snug mt-0.5">{st.why}</p>}
                  {st.url && (
                    <a href={st.url.startsWith("http") ? st.url : undefined} target="_blank" rel="noreferrer"
                      className="inline-flex items-center gap-1 text-[10px] font-bold mt-0.5 hover:underline" style={{ color: PRIMARY }}>
                      {path(st.url)} {st.url.startsWith("http") && <ExternalLink size={9} />}
                    </a>
                  )}
                </div>
              </li>
            ))}
          </ol>
          {state.data.impact && <p className="text-[10px] text-zinc-500 mt-2 pt-2 border-t border-emerald-100"><b className="text-zinc-700">Impact:</b> {state.data.impact}</p>}
          {state.data.generated_at && <p className="text-[9px] text-zinc-400 mt-1.5">Written {ago(state.data.generated_at)}</p>}
          <div className="mt-3">
            {taskState?.task ? (
              <Link to={`/dashboard/tasks/${taskState.task.key}`}
                className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-bold rounded-lg border border-emerald-200 bg-white hover:bg-emerald-50"
                style={{ color: PRIMARY }}>
                <Check size={12} /> {taskState.existing ? "Already a task" : "Created"}: {taskState.task.key} · Open
              </Link>
            ) : (
              <button onClick={createTask} disabled={taskState?.busy || !state.data.steps.length}
                title="Save these steps as a task: checklist, priority from the lead tier, topic queries linked for before/after tracking"
                className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-bold text-white rounded-lg disabled:opacity-50"
                style={{ background: PRIMARY }}>
                <ListPlus size={12} /> {taskState?.busy ? "Creating task…" : "Create task from these steps"}
              </button>
            )}
            {taskState?.error && <p className="text-[10px] text-red-600 mt-1">{taskState.error}</p>}
          </div>
        </>
      )}
    </div>
  );
}

function TierDot({ tier }) {
  return <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: (TIER[tier] || TIER.unscored).color }} title={(TIER[tier] || TIER.unscored).label} />;
}

export default function SearchDemandGraph() {
  const navigate = useNavigate();
  const graphRef = useRef(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLinks, setShowLinks] = useState(true);
  const [showMissing, setShowMissing] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    let alive = true;
    api.getSearchDemandGraph()
      .then(d => alive && setData(d))
      .catch(e => { console.error("[SearchDemandGraph] load error", e); alive && setData(null); })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);

  const { nodes, allEdges } = useMemo(() => {
    if (!data) return { nodes: [], allEdges: [] };
    const maxPriority = Math.max(1, ...data.nodes.filter(n => n.type === "topic").map(n => n.priority || 0));
    const topicColor = {};
    const nodes = data.nodes.map(n => {
      if (n.type === "topic") {
        const color = (TIER[n.lead_tier] || TIER.unscored).color;
        topicColor[n.id] = color;
        return {
          id: n.id, label: n.coverage_status === "gap" ? `${n.title} · no page` : n.title,
          fill: color, size: Math.max(10, Math.round(10 + 18 * ((n.priority || 0) / maxPriority))), data: n,
        };
      }
      return {
        id: n.id, label: n.is_key_page ? `★ ${n.title}` : n.title,
        fill: n.is_old ? OLD_PAGE_COLOR : n.is_key_page ? KEY_PAGE_COLOR : PAGE_COLOR,
        size: n.is_key_page ? 9 : n.is_old ? 7 : 5, data: n,
      };
    });
    const allEdges = data.edges.map((e, i) => ({
      id: `${e.type}:${i}`, source: e.source, target: e.target, edgeType: e.type,
      ...(e.type === "serves"
        ? { fill: topicColor[e.source] || PRIMARY, size: 1 + 4 * (e.share || 0), opacity: 0.5, interpolation: "linear" }
        : e.type === "link"
          ? { fill: LINK_COLOR, size: 1.5, interpolation: "curved" }
          : { fill: MISSING_COLOR, size: 1.5, dashed: true, interpolation: "curved" }),
    }));
    return { nodes, allEdges };
  }, [data]);

  const edges = useMemo(() => allEdges.filter(e =>
    e.edgeType === "serves" || (e.edgeType === "link" && showLinks) || (e.edgeType === "missing_link" && showMissing),
  ), [allEdges, showLinks, showMissing]);

  const { selections, actives, onNodeClick, onCanvasClick, onNodePointerOver, onNodePointerOut, clearSelections } =
    useSelection({ ref: graphRef, nodes, edges, type: "single", pathHoverType: "all", focusOnSelect: false });

  const nodeById = useMemo(() => Object.fromEntries(nodes.map(n => [n.id, n])), [nodes]);
  const nodeIds = useMemo(() => new Set(nodes.map(n => n.id)), [nodes]);

  useEffect(() => {
    if (!nodes.length) return;
    const t = setTimeout(() => graphRef.current?.fitNodesInView(), 600);
    return () => clearTimeout(t);
  }, [nodes]);

  const focus = useCallback((id) => {
    if (!nodeById[id]) return;
    graphRef.current?.centerGraph([id]);
    clearSelections();
    setSelected(nodeById[id]);
  }, [nodeById, clearSelections]);

  if (loading) {
    return <div className="h-96 flex items-center justify-center text-sm text-zinc-400">Building search-demand map…</div>;
  }
  const stats = data?.stats || {};
  if (!data || !stats.has_topics) {
    return (
      <div className="h-96 flex flex-col items-center justify-center text-center bg-white rounded-xl border border-zinc-100">
        <Network size={44} color="#CCC" />
        <p className="text-sm font-bold text-zinc-700 mt-4">No search topics yet</p>
        <p className="text-xs text-zinc-500 mt-1 max-w-sm">
          The map is built from your Search Console queries. Run Gap Analysis, then Crawl Site to add the links between pages.
        </p>
        <Link to="/dashboard/search-performance?tab=queries" className="mt-3 text-xs font-bold hover:underline" style={{ color: PRIMARY }}>
          Run Gap Analysis →
        </Link>
      </div>
    );
  }

  const todos = data.todos;
  const servedBy = (pageId) => data.edges.filter(e => e.type === "serves" && e.target === pageId)
    .map(e => ({ node: nodeById[e.source], impressions: e.impressions })).filter(x => x.node);

  return (
    <>
      {(!stats.has_links || !stats.has_key_pages) && (
        <div className="mb-4 px-4 py-2.5 bg-blue-50 rounded-xl text-xs text-blue-800 flex flex-wrap gap-x-4 gap-y-1">
          {!stats.has_links && (
            <span>No links between pages yet: <Link to="/dashboard/sitemap" className="font-bold underline">Crawl Site</Link> in Visual Sitemap.</span>
          )}
          {!stats.has_key_pages && (
            <span>Add your money pages under <Link to="/dashboard/settings/business" className="font-bold underline">Business Context → Key Pages</Link> to find missing paths to leads.</span>
          )}
        </div>
      )}

      <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <TodoCard title="Write a pillar page" icon={PenLine} items={todos.write_pillar}
          empty="Every lead topic has a page."
          render={t => (
            <button key={t.topic_id} onClick={() => focus(`topic:${t.topic_id}`)}
              className="flex items-center gap-2 bg-zinc-50 hover:bg-zinc-100 rounded-lg p-2.5 text-left">
              <TierDot tier={t.lead_tier} />
              <span className="text-xs font-semibold text-zinc-800 truncate flex-1">{t.topic}</span>
              <span className="text-[10px] text-zinc-400 whitespace-nowrap">{(t.impressions || 0).toLocaleString()} impr</span>
            </button>
          )} />
        <TodoCard title="Link to a key page" icon={Link2} items={todos.link_to_key_page}
          empty={stats.has_key_pages ? "Lead topic pages already link to a key page." : "No key pages set yet."}
          render={t => (
            <button key={t.topic_id} onClick={() => focus(t.from_url)}
              className="flex flex-col gap-0.5 bg-zinc-50 hover:bg-zinc-100 rounded-lg p-2.5 text-left">
              <span className="flex items-center gap-2 text-xs font-semibold text-zinc-800 truncate"><TierDot tier={t.lead_tier} />{t.topic}</span>
              <span className="text-[10px] text-zinc-500 truncate">{path(t.from_url)} <span style={{ color: MISSING_COLOR }}>→</span> {path(t.to_url)}</span>
            </button>
          )} />
        <TodoCard title="Redirect old pages" icon={CornerUpRight} items={todos.redirect_old}
          empty="No lead topic lands on an old page."
          render={t => (
            <button key={t.topic_id} onClick={() => focus(t.old_url)}
              className="flex flex-col gap-0.5 bg-red-50 hover:bg-red-100 rounded-lg p-2.5 text-left">
              <span className="flex items-center gap-2 text-xs font-semibold text-zinc-800 truncate"><TierDot tier={t.lead_tier} />{t.topic}</span>
              <span className="text-[10px] text-red-700 truncate">lands on {path(t.old_url)}{t.newer_url ? ` → redirect to ${path(t.newer_url)}` : ""}</span>
            </button>
          )} />
      </div>

      <div className="flex items-center gap-4 mb-2">
        <label className="flex items-center gap-1.5 cursor-pointer select-none">
          <input type="checkbox" checked={showLinks} onChange={e => setShowLinks(e.target.checked)} className="w-3.5 h-3.5" style={{ accentColor: LINK_COLOR }} />
          <span className="text-xs font-bold" style={{ color: LINK_COLOR }}>Links between pages</span>
        </label>
        <label className="flex items-center gap-1.5 cursor-pointer select-none">
          <input type="checkbox" checked={showMissing} onChange={e => setShowMissing(e.target.checked)} className="w-3.5 h-3.5" style={{ accentColor: MISSING_COLOR }} />
          <span className="text-xs font-bold" style={{ color: MISSING_COLOR }}>Missing links to key pages</span>
        </label>
      </div>

      <div className="flex gap-4">
        <div className="flex-1 bg-white rounded-xl border border-zinc-100 shadow-sm overflow-hidden relative" style={{ height: 580 }}>
          <GraphCanvas
            ref={graphRef}
            nodes={nodes}
            edges={edges}
            selections={selections}
            actives={actives}
            animated
            onNodeClick={(n) => { onNodeClick(n); setSelected(n); }}
            onCanvasClick={(e) => { onCanvasClick(e); setSelected(null); }}
            onNodePointerOver={(n) => nodeIds.has(n.id) && onNodePointerOver(n)}
            onNodePointerOut={onNodePointerOut}
            layoutType="forceDirected2d"
            cameraMode="pan"
            draggable
            labelType="auto"
            sizingType="none"
            minNodeSize={4}
            maxNodeSize={30}
            theme={THEME}
          />
        </div>

        {selected && (
          <div className="w-80 flex-shrink-0 bg-white rounded-xl border border-zinc-100 shadow-sm p-4 flex flex-col gap-3 overflow-y-auto" style={{ height: 580 }}>
            <div className="flex items-center justify-between">
              <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">
                {selected.data.type === "topic" ? "Search topic" : selected.data.is_key_page ? "Key page" : "Page"}
              </p>
              <button onClick={() => { setSelected(null); clearSelections(); }} className="text-zinc-300 hover:text-zinc-600"><X size={14} /></button>
            </div>
            <p className="text-sm font-semibold text-zinc-900 leading-snug">{selected.data.title}</p>
            <NodeInsight key={selected.id} nodeId={selected.id} />

            {selected.data.type === "topic" ? (
              <>
                {selected.data.problem && <p className="text-[11px] text-zinc-500">{selected.data.problem}</p>}
                <div className="grid grid-cols-2 gap-2">
                  <div className="bg-zinc-50 rounded-lg p-2.5">
                    <p className="text-xs font-bold" style={{ color: (TIER[selected.data.lead_tier] || TIER.unscored).color }}>
                      {(TIER[selected.data.lead_tier] || TIER.unscored).label}
                    </p>
                    <p className="text-[10px] text-zinc-500 uppercase tracking-wide mt-0.5">Lead potential</p>
                  </div>
                  <div className="bg-zinc-50 rounded-lg p-2.5">
                    <p className="text-xs font-bold text-zinc-900">{(selected.data.impressions || 0).toLocaleString()}</p>
                    <p className="text-[10px] text-zinc-500 uppercase tracking-wide mt-0.5">Impressions</p>
                  </div>
                </div>
                {selected.data.next_move && (
                  <p className="text-[11px] text-zinc-700"><span className="text-zinc-400">Next move:</span> {MOVES[selected.data.next_move] || selected.data.next_move}</p>
                )}
                <button onClick={() => openSearchTopic(navigate, selected.data.topic_id)}
                  className="mt-auto flex items-center justify-center gap-1.5 px-3 py-2 bg-zinc-900 text-white text-xs font-bold rounded-lg hover:bg-zinc-700">
                  Open in Search topics <ArrowRight size={12} />
                </button>
              </>
            ) : (
              <>
                <p className="text-[10px] text-zinc-400 break-all">{selected.data.url}</p>
                {selected.data.is_old && (
                  <p className="text-[11px] text-red-700 bg-red-50 rounded-lg p-2">Looks like an old version still live. See Visual Sitemap → Pages to review.</p>
                )}
                {selected.data.key_purpose && <p className="text-[11px] text-zinc-600">{selected.data.key_purpose}</p>}
                <div>
                  <p className="text-[10px] text-zinc-400 uppercase tracking-wide mb-1.5">Serves topics</p>
                  {servedBy(selected.id).length === 0
                    ? <p className="text-[11px] text-zinc-400">No search topic lands here.</p>
                    : servedBy(selected.id).map(s => (
                      <button key={s.node.id} onClick={() => focus(s.node.id)} className="w-full flex items-center gap-1.5 py-0.5 text-left">
                        <TierDot tier={s.node.data.lead_tier} />
                        <span className="text-xs text-zinc-600 truncate flex-1">{s.node.data.title}</span>
                        <span className="text-[10px] text-zinc-400">{(s.impressions || 0).toLocaleString()}</span>
                      </button>
                    ))}
                </div>
                <a href={selected.data.url} target="_blank" rel="noreferrer"
                  className="mt-auto flex items-center justify-center gap-1.5 px-3 py-2 bg-zinc-900 text-white text-xs font-bold rounded-lg hover:bg-zinc-700">
                  <ExternalLink size={12} /> Open live page
                </a>
              </>
            )}
            <button onClick={() => graphRef.current?.centerGraph([selected.id])}
              className="flex items-center justify-center gap-1 px-3 py-1.5 border border-zinc-200 text-zinc-600 text-[11px] font-bold rounded-lg hover:bg-zinc-50">
              <Crosshair size={11} /> Center
            </button>
          </div>
        )}
      </div>

      <div className="mt-4 bg-white rounded-xl border border-zinc-100 p-4 shadow-sm flex flex-wrap gap-x-5 gap-y-2 text-xs text-zinc-500">
        {["high", "medium", "low"].map(k => (
          <span key={k} className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ background: TIER[k].color }} /> {TIER[k].label} topic</span>
        ))}
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ background: KEY_PAGE_COLOR }} /> ★ Key page</span>
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ background: OLD_PAGE_COLOR }} /> Old page still live</span>
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ background: PAGE_COLOR }} /> Page</span>
        <span className="flex items-center gap-1.5"><span className="w-6 h-0.5" style={{ background: LINK_COLOR }} /> Link</span>
        <span className="flex items-center gap-1.5"><span className="w-6 h-0.5" style={{ borderTop: `2px dashed ${MISSING_COLOR}` }} /> Missing link to key page</span>
        <span className="ml-auto text-zinc-400">{stats.topics} topics · {stats.pages} pages · {stats.links} links · hub size = priority</span>
      </div>
    </>
  );
}
